// /industrial-estates — TH hub for Thailand industrial estates (Phase P-INDUSTRIAL-ESTATES)
// B2B targeting: foreign-invested factories needing notary, translation, work-permit, MFA legalization.
import { createFileRoute, Link } from "@tanstack/react-router";
import { INDUSTRIAL_ESTATES } from "@/data/industrial-estates";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { Building2, MapPin, Phone, MessageCircle, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/industrial-estates/")({
  head: () => {
    const thPath = "/industrial-estates";
    const enPath = "/en/industrial-estates";
    const title = "นิคมอุตสาหกรรมไทย 20+ แห่ง — รับ Notary · แปล · Work Permit · MFA | NYC";
    const desc = "บริการครบวงจรสำหรับโรงงานต่างชาติในนิคมอุตสาหกรรมไทย (EEC, อมตะ, WHA, มาบตาพุด, ลาดกระบัง, นวนคร ฯลฯ) — รับเอกสารถึงโรงงาน, แปล EN/JP/CN/KR — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";
    const url = `${SITE}${thPath}`;
    return {
      meta: [
        { title: ctaTitle(title, "/industrial-estates", "th") }, { name: "description", content: ctaDesc(desc, "/industrial-estates", "th")},
        { property: "og:title", content: ctaTitle(title, "/industrial-estates", "th")}, { property: "og:description", content: ctaDesc(desc, "/industrial-estates", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "นิคมอุตสาหกรรม, EEC, อมตะ, WHA, มาบตาพุด, ลาดกระบัง, นวนคร, แหลมฉบัง, work permit นิคม, BOI report, แปลเอกสารโรงงาน, MFA นิคม" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#collection`,
            name: title, description: desc, url,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: INDUSTRIAL_ESTATES.length,
              itemListElement: INDUSTRIAL_ESTATES.map((e, i) => ({
                "@type": "ListItem", position: i + 1, name: e.nameTh,
                url: `${SITE}/industrial-estates/${e.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { "@type": "Question", name: "NYC ให้บริการในนิคมอุตสาหกรรมไหนบ้าง?",
                acceptedAnswer: { "@type": "Answer", text: `ครอบคลุม ${INDUSTRIAL_ESTATES.length} นิคมหลักทั่วประเทศ — EEC (อมตะ, WHA, Hemaraj, มาบตาพุด, แหลมฉบัง, Gateway City), ภาคกลาง (ลาดกระบัง, บางปู, บางพลี, นวนคร, โรจนะ), ภาคเหนือ (ลำพูน), อีสาน (สุรนารี) และภาคใต้ (สงขลา).` } },
              { "@type": "Question", name: "บริการอะไรที่โรงงานในนิคมใช้มากที่สุด?",
                acceptedAnswer: { "@type": "Answer", text: "Work Permit + Visa สำหรับวิศวกรญี่ปุ่น/จีน, แปล Technical Spec / ISO Manual, รับรอง MFA สำหรับ Export Cert / COO, Notary สำหรับ POA + Board Resolution, และล่ามภาษาในที่ตรวจประจำปี." } },
              { "@type": "Question", name: "รับเอกสารถึงโรงงานในนิคมไหม?",
                acceptedAnswer: { "@type": "Answer", text: "ใช่ — NYC มี courier ประจำในเส้นทาง EEC, ลาดกระบัง–สุวรรณภูมิ, นวนคร–ดอนเมือง และ Lamphun–CNX ทุกวันทำการ ค่าส่งฟรีเมื่อยอดงานเกิน 5,000 บาท." } },
              { "@type": "Question", name: "ใช้เวลาเท่าไหร่กว่างานจะเสร็จ?",
                acceptedAnswer: { "@type": "Answer", text: "Notary same-day, แปล Technical 1-2 วัน, รับรอง MFA 2-3 วันทำการ, work permit ใหม่ 7-10 วันราชการ ขึ้นกับสำนักงานจัดหางานพื้นที่." } },
            ],
          }),
        },
      ],
    };
  },
  component: IndustrialEstatesHubTh,
});

function IndustrialEstatesHubTh() {
  const byRegion = INDUSTRIAL_ESTATES.reduce<Record<string, typeof INDUSTRIAL_ESTATES>>((acc, e) => {
    (acc[e.region] ||= []).push(e); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Building2 className="mr-1 inline h-3 w-3" /> B2B · นิคมอุตสาหกรรม
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            บริการครบวงจรในนิคมอุตสาหกรรมไทย {INDUSTRIAL_ESTATES.length}+ แห่ง
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Notary · Certified Translation (EN/JP/CN/KR) · Work Permit · BOI Report · MFA Legalisation —
            ทีม NYC วิ่งรับเอกสารถึงโรงงานใน EEC, ภาคกลาง, ภาคเหนือ, อีสานและภาคใต้ทุกวันทำการ
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">โรงงานในนิคมจะใช้บริการ NYC อย่างไร?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              ติดต่อ LINE @nyc-notary หรือโทร 092-242-9293 — ทีมเข้ารับเอกสารถึงโรงงาน วันถัดไปได้งานแปลพร้อม Notary, ส่ง MFA Chaeng Wattana ภายใน 3 วัน, คืนเอกสารพร้อม Apostille/Embassy ภายใน 5-7 วัน. ตรวจเอกสารครบตามเกณฑ์หน่วยงานก่อนยื่น
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เลือกนิคมอุตสาหกรรมของคุณ</h2>
          <p className="mt-2 text-sm text-muted-foreground">แบ่งตามภูมิภาค · กดเพื่อดูบริการเฉพาะนิคม + ราคา + เส้นทาง courier</p>
          <div className="mt-8 space-y-10">
            {Object.entries(byRegion).map(([region, list]) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary"><MapPin className="mr-1 inline h-4 w-4" />{region}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((e) => (
                    <Link
                      key={e.slug}
                      to="/industrial-estates/$slug"
                      params={{ slug: e.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="industrial-hub-th"
                    >
                      <div className="font-bold">{e.nameTh}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{e.province} · {e.zoneType}</div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{e.industries.slice(0, 3).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        ดูบริการ <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการที่ใช้บ่อยในนิคม</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Work Permit + BOI Visa", d: "วิศวกร JP/CN/KR/IN — turnaround 7-10 วัน", to: "/work-permit" as const },
              { t: "Technical Translation", d: "ISO Manual, MSDS, HAZOP, ECO — ISO 17100", to: "/translate" as const },
              { t: "MFA + Embassy Legalisation", d: "COO, Export Cert, POA — 3-5 วัน", to: "/translate/mfa" as const },
              { t: "Notary (POA, Board Resolution)", d: "Notarial Services Attorney รับรองในวัน", to: "/notary" as const },
              { t: "Apostille (Hague)", d: "สำหรับ JV/Partner ในประเทศ Hague", to: "/apostille" as const },
              { t: "APEC Business Card (ABTC)", d: "ผู้บริหารโรงงานเดินทาง 21 economies", to: "/apec" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
